<template>
  <section
    class="base-gallery animate__animated animate__fadeIn"
    role="dialog"
    aria-modal="true"
    :aria-label="$t('unit_page.un_v_all')"
    tabindex="-1"
    @touchstart.passive="onTouchStart"
    @touchmove.passive="onTouchMove"
    @touchend="onTouchEnd"
  >
    <header class="base-gallery-head">
      <span class="head-spacer" aria-hidden="true" />
      <span class="font--b1 text-white-contrast counter">
        {{ currentImgIndex + 1 }} / {{ totalImg }}
      </span>
      <button
        v-close-popup
        class="font--b1 text-white-contrast close-btn"
        type="button"
        aria-label="Close gallery"
      >
        <Suspense>
          <base-icon
            name="base/Close"
            size="20px"
            color="var(--white-contrast)"
          />
        </Suspense>
      </button>
    </header>

    <div class="base-gallery-stage">
      <button
        v-if="hasMultiple"
        class="control control--prev"
        type="button"
        aria-label="Previous image"
        @click="prevImage"
      >
        <Suspense>
          <base-icon
            name="base/ChevronLeft"
            size="22px"
            color="var(--white-contrast)"
          />
        </Suspense>
      </button>

      <div class="image-frame">
        <transition name="gallery-fade" mode="out-in">
          <NuxtImg
            v-if="currentSrc && photosLoaded[currentImgIndex]"
            :key="currentImgIndex"
            :src="currentSrc"
            :alt="`Image ${currentImgIndex + 1} of ${totalImg}`"
            format=""
            draggable="false"
          />
          <base-loader v-else key="loader" />
        </transition>
      </div>

      <button
        v-if="hasMultiple"
        class="control control--next"
        type="button"
        aria-label="Next image"
        @click="nextImage"
      >
        <Suspense>
          <base-icon
            name="base/ChevronRight"
            size="22px"
            color="var(--white-contrast)"
          />
        </Suspense>
      </button>
    </div>

    <footer
      v-if="hasMultiple"
      ref="thumbsStripRef"
      class="base-gallery-thumbs"
      aria-label="Image thumbnails"
    >
      <button
        v-for="(item, idx) in normalized"
        :key="idx"
        ref="thumbItemsRef"
        type="button"
        class="thumb"
        :class="{ 'thumb--active': idx === currentImgIndex }"
        :aria-current="idx === currentImgIndex ? 'true' : undefined"
        :aria-label="`Go to image ${idx + 1}`"
        @click="goTo(idx)"
      >
        <img
          :src="item.thumb"
          :alt="`Thumbnail ${idx + 1}`"
          loading="lazy"
          draggable="false"
        >
      </button>
    </footer>
  </section>
</template>

<script setup lang="ts">
import type { FileResponseDTO } from "~/services/swagger/Api";
import { getThumbnail } from "~/utilities/helpers/thumbnails/getThumbnail";

type GalleryItem = string | Partial<FileResponseDTO>;

const props = defineProps<{
  data: { imgs: GalleryItem[]; pickedPhotoIndex?: number };
}>();

interface NormalizedImg {
  full: string;
  thumb: string;
}

const SWIPE_THRESHOLD = 40;

const normalized = computed<NormalizedImg[]>(() => {
  const list = props.data?.imgs ?? [];
  return list
    .map((item) => {
      if (typeof item === "string") {
        return { full: item, thumb: item };
      }
      const full = item?.url || "";
      const thumb = getThumbnail(item, "thumbnail_130x100") || full;
      return { full, thumb };
    })
    .filter((i) => Boolean(i.full));
});

const totalImg = computed(() => normalized.value.length);
const hasMultiple = computed(() => totalImg.value > 1);

const currentImgIndex = ref(props.data?.pickedPhotoIndex ?? 0);
const photosLoaded = ref<boolean[]>(Array(normalized.value.length).fill(false));

const currentSrc = computed(
  () => normalized.value[currentImgIndex.value]?.full || ""
);

const preload = (index: number) => {
  if (index < 0 || index >= normalized.value.length) return;
  if (photosLoaded.value[index]) return;
  const src = normalized.value[index]?.full;
  if (!src) return;
  const img = new Image();
  img.src = src;
  img.onload = () => {
    photosLoaded.value[index] = true;
  };
  img.onerror = () => {
    photosLoaded.value[index] = true;
  };
};

const preloadAround = (index: number) => {
  preload(index);
  preload((index + 1) % normalized.value.length);
  preload(
    (index - 1 + normalized.value.length) % normalized.value.length
  );
};

const thumbsStripRef = ref<HTMLElement | null>(null);
const thumbItemsRef = ref<HTMLElement[]>([]);

const scrollActiveThumbIntoView = () => {
  const strip = thumbsStripRef.value;
  const items = thumbItemsRef.value;
  if (!strip || !items?.length) return;
  const target = items[currentImgIndex.value];
  if (!target) return;
  const targetCenter = target.offsetLeft + target.offsetWidth / 2;
  const left = targetCenter - strip.clientWidth / 2;
  strip.scrollTo({ left, behavior: "smooth" });
};

const goTo = (idx: number) => {
  if (idx < 0 || idx >= normalized.value.length) return;
  currentImgIndex.value = idx;
};

const nextImage = () => {
  if (!hasMultiple.value) return;
  currentImgIndex.value =
    (currentImgIndex.value + 1) % normalized.value.length;
};

const prevImage = () => {
  if (!hasMultiple.value) return;
  currentImgIndex.value =
    (currentImgIndex.value - 1 + normalized.value.length) %
    normalized.value.length;
};

watch(
  () => props.data?.imgs,
  () => {
    currentImgIndex.value = props.data?.pickedPhotoIndex ?? 0;
    photosLoaded.value = Array(normalized.value.length).fill(false);
    preloadAround(currentImgIndex.value);
  },
  { immediate: true }
);

watch(currentImgIndex, (idx) => {
  preloadAround(idx);
  nextTick(scrollActiveThumbIntoView);
});

const touchStartX = ref(0);
const touchStartY = ref(0);
const touchDeltaX = ref(0);
const touchDeltaY = ref(0);

const onTouchStart = (e: TouchEvent) => {
  const t = e.touches[0];
  touchStartX.value = t.clientX;
  touchStartY.value = t.clientY;
  touchDeltaX.value = 0;
  touchDeltaY.value = 0;
};

const onTouchMove = (e: TouchEvent) => {
  const t = e.touches[0];
  touchDeltaX.value = t.clientX - touchStartX.value;
  touchDeltaY.value = t.clientY - touchStartY.value;
};

const onTouchEnd = () => {
  if (Math.abs(touchDeltaX.value) <= Math.abs(touchDeltaY.value)) return;
  if (Math.abs(touchDeltaX.value) < SWIPE_THRESHOLD) return;
  if (touchDeltaX.value < 0) {
    nextImage();
  } else {
    prevImage();
  }
};

const onKeydown = (e: KeyboardEvent) => {
  if (e.key === "ArrowRight") {
    e.preventDefault();
    nextImage();
  } else if (e.key === "ArrowLeft") {
    e.preventDefault();
    prevImage();
  }
};

onMounted(() => {
  window.addEventListener("keydown", onKeydown);
  nextTick(scrollActiveThumbIntoView);
});

onBeforeUnmount(() => {
  window.removeEventListener("keydown", onKeydown);
});
</script>

<style scoped lang="scss">
.base-gallery {
  display: grid;
  grid-template-rows: auto 1fr auto;
  width: 100%;
  height: 100%;
  background-color: var(--black-monochrome, #000);
  overflow: hidden;
  padding: 20px 24px;
  gap: 12px;

  &-head {
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    align-items: center;
    width: 100%;
    max-width: 1400px;
    margin: 0 auto;
    padding: 0 4px;

    .head-spacer {
      display: block;
    }

    .counter {
      justify-self: center;
    }

    .close-btn {
      @include drop-button-styles;
      justify-self: end;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 40px;
      height: 40px;
      border-radius: 50%;
      background-color: rgba(255, 255, 255, 0.08);
      transition: background-color 0.2s ease, transform 0.2s ease;

      &:hover {
        background-color: rgba(255, 255, 255, 0.18);
      }

      &:active {
        transform: scale(0.94);
      }
    }
  }

  &-stage {
    position: relative;
    display: grid;
    grid-template-columns: auto minmax(0, 1fr) auto;
    align-items: center;
    justify-items: center;
    gap: 16px;
    width: 100%;
    max-width: 1400px;
    height: 100%;
    margin: 0 auto;
    min-height: 0;

    .image-frame {
      position: relative;
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      min-height: 0;
      overflow: hidden;
      user-select: none;

      img {
        display: block;
        max-width: 100%;
        max-height: 100%;
        width: auto;
        height: auto;
        object-fit: contain;
        border-radius: 8px;
      }
    }

    .control {
      @include drop-button-styles;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 48px;
      height: 48px;
      border-radius: 50%;
      background-color: rgba(255, 255, 255, 0.08);
      border: 1px solid rgba(255, 255, 255, 0.16);
      backdrop-filter: blur(6px);
      transition: background-color 0.2s ease, transform 0.2s ease,
        border-color 0.2s ease;

      &:hover {
        background-color: rgba(255, 255, 255, 0.18);
        border-color: rgba(255, 255, 255, 0.32);
      }

      &:active {
        transform: scale(0.94);
      }

      &:focus-visible {
        outline: 2px solid var(--white-contrast);
        outline-offset: 2px;
      }
    }
  }

  &-thumbs {
    display: flex;
    gap: 8px;
    width: 100%;
    max-width: 1400px;
    margin: 0 auto;
    padding: 8px 4px 4px;
    overflow-x: auto;
    overflow-y: hidden;
    scroll-behavior: smooth;
    scrollbar-width: thin;
    scrollbar-color: rgba(255, 255, 255, 0.25) transparent;

    &::-webkit-scrollbar {
      height: 6px;
    }
    &::-webkit-scrollbar-track {
      background: transparent;
    }
    &::-webkit-scrollbar-thumb {
      background: rgba(255, 255, 255, 0.25);
      border-radius: 3px;
    }

    .thumb {
      @include drop-button-styles;
      flex: 0 0 auto;
      width: 96px;
      height: 64px;
      border-radius: 6px;
      overflow: hidden;
      position: relative;
      border: 2px solid transparent;
      opacity: 0.55;
      transition: opacity 0.2s ease, border-color 0.2s ease,
        transform 0.2s ease;

      img {
        display: block;
        width: 100%;
        height: 100%;
        object-fit: cover;
      }

      &:hover {
        opacity: 0.9;
        transform: translateY(-1px);
      }

      &--active {
        opacity: 1;
        border-color: var(--white-contrast, #fff);
      }

      &:focus-visible {
        outline: 2px solid var(--white-contrast);
        outline-offset: 2px;
      }
    }
  }
}

.gallery-fade-enter-active,
.gallery-fade-leave-active {
  transition: opacity 0.25s ease;
}
.gallery-fade-enter-from,
.gallery-fade-leave-to {
  opacity: 0;
}

@media (max-width: 768px) {
  .base-gallery {
    padding: 12px 0;
    gap: 8px;

    &-head {
      padding: 0 12px;
    }

    &-stage {
      grid-template-columns: minmax(0, 1fr);
      padding: 0 12px;
      gap: 0;

      .control {
        display: none;
      }

      .image-frame img {
        border-radius: 6px;
      }
    }

    &-thumbs {
      padding: 4px 12px 8px;
      gap: 6px;

      .thumb {
        width: 64px;
        height: 48px;
        border-width: 2px;
        border-radius: 4px;
      }
    }
  }
}
</style>
